import {
  CartErrorEvent,
  CartLinesUpdateEvent,
} from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

class ChorelessProduct extends HTMLElement {
  connectedCallback() {
    this.variantSelect = this.querySelector('[data-ch-variant-select]');
    this.variantId = this.querySelector('[data-ch-variant-id]');
    this.priceElements = this.querySelectorAll('[data-ch-price]');
    this.compareElements = this.querySelectorAll('[data-ch-compare]');
    this.savingsElement = this.querySelector('[data-ch-savings]');
    this.availabilityElement = this.querySelector('[data-ch-availability]');
    this.availabilityLabel = this.querySelector('[data-ch-availability-label]');
    this.addButton = this.querySelector('[data-ch-add]');
    this.addButtonLabel = this.querySelector('[data-ch-add-label]');
    this.dynamicCheckout = this.querySelector('[data-ch-dynamic-checkout]');
    this.stockMessage = this.querySelector('[data-ch-stock-message]');
    this.mainImage = this.querySelector('.ch-product__image-stage img, .ch-product__main-image');
    this.mainImage?.classList.add('ch-product__main-image');
    this.stageBadge = this.querySelector('[data-ch-stage-badge]');
    this.quantityInput = this.querySelector('[data-ch-quantity-input]');
    this.quantityMinus = this.querySelector('[data-ch-quantity-minus]');
    this.quantityPlus = this.querySelector('[data-ch-quantity-plus]');
    this.productForm = this.querySelector('form[action*="/cart/add"]');
    this.isAvailable = this.availabilityElement?.dataset.available === 'true';
    this.isSubmitting = false;
    this.optionButtons = Array.from(this.querySelectorAll('[data-ch-option-button]'));
    this.bundleOptions = Array.from(this.querySelectorAll('[data-ch-bundle-option]'));
    this.thumbnails = Array.from(this.querySelectorAll('[data-ch-thumbnail]'));
    this.mobileGallery = this.querySelector('[data-ch-mobile-gallery]');
    this.mobileSlides = Array.from(this.querySelectorAll('[data-ch-mobile-slide]'));
    this.mobileImageCounter = this.querySelector('[data-ch-mobile-image-index]');
    this.mobileScrollFrame = null;
    this.isFluffRoller = this.dataset.productKind === 'fluff-roller';
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.currentImageIndex = Math.max(
      this.thumbnails.findIndex((thumbnail) => thumbnail.getAttribute('aria-current') === 'true'),
      0
    );

    this.variantSelect?.addEventListener('change', this.handleVariantChange);
    this.quantityMinus?.addEventListener('click', this.decreaseQuantity);
    this.quantityPlus?.addEventListener('click', this.increaseQuantity);
    this.productForm?.addEventListener('submit', this.handleSubmit);
    this.mobileGallery?.addEventListener('scroll', this.handleMobileGalleryScroll, {
      passive: true,
    });
    this.optionButtons.forEach((button) => {
      button.addEventListener('click', this.handleOptionButtonClick);
    });
    this.bundleOptions.forEach((button) => {
      button.addEventListener('click', this.handleBundleOptionClick);
    });

    this.thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', this.handleThumbnailClick);
    });
    const initialVariant = this.variantSelect?.selectedOptions[0];
    if (initialVariant) this.syncOptionButtons(initialVariant);
    if (this.isFluffRoller && initialVariant && !this.isGrayVariant(initialVariant)) {
      const initialGalleryIndex = Number.parseInt(initialVariant.dataset.galleryIndex || '-1', 10);
      if (initialGalleryIndex >= 0) this.showImageAtIndex(initialGalleryIndex);
    }
    this.updateImageCounter();

    window.requestAnimationFrame(() => {
      this.syncMobileGallery(this.currentImageIndex, false);
    });
  }

  disconnectedCallback() {
    this.variantSelect?.removeEventListener('change', this.handleVariantChange);
    this.quantityMinus?.removeEventListener('click', this.decreaseQuantity);
    this.quantityPlus?.removeEventListener('click', this.increaseQuantity);
    this.productForm?.removeEventListener('submit', this.handleSubmit);
    this.mobileGallery?.removeEventListener('scroll', this.handleMobileGalleryScroll);
    this.optionButtons.forEach((button) => {
      button.removeEventListener('click', this.handleOptionButtonClick);
    });
    this.bundleOptions.forEach((button) => {
      button.removeEventListener('click', this.handleBundleOptionClick);
    });

    this.thumbnails.forEach((thumbnail) => {
      thumbnail.removeEventListener('click', this.handleThumbnailClick);
    });

    if (this.mobileScrollFrame) {
      window.cancelAnimationFrame(this.mobileScrollFrame);
    }
  }

  handleVariantChange = () => {
    const option = this.variantSelect?.selectedOptions[0];
    if (!option) return;

    const isAvailable = option.dataset.available === 'true';
    const compareAtPrice = option.dataset.compareAtPrice || '';
    const savings = Number.parseInt(option.dataset.savings || '0', 10);
    const inventoryQuantity = Number.parseInt(option.dataset.inventoryQuantity || '0', 10);
    const inventoryManaged = option.dataset.inventoryManaged === 'true';
    const isLowStock =
      isAvailable && inventoryManaged && inventoryQuantity > 0 && inventoryQuantity < 5;
    this.isAvailable = isAvailable;

    if (this.variantId) this.variantId.value = option.value;

    this.priceElements.forEach((element) => {
      element.textContent = option.dataset.price || '';
    });

    this.compareElements.forEach((element) => {
      element.textContent = compareAtPrice;
      element.hidden = !compareAtPrice;
    });

    if (this.savingsElement) {
      this.savingsElement.textContent = savings > 0 ? `Save ${savings}%` : '';
      this.savingsElement.hidden = savings <= 0;
    }

    if (this.availabilityElement) {
      this.availabilityElement.dataset.available = String(isAvailable);
      this.availabilityElement.dataset.lowStock = String(isLowStock);
    }

    if (this.availabilityLabel) {
      this.availabilityLabel.textContent = !isAvailable
        ? 'Out of stock'
        : isLowStock
          ? `Only ${inventoryQuantity} left in stock`
          : this.isFluffRoller
            ? 'In stock'
            : 'Ready to order';
    }

    if (this.addButton) {
      this.addButton.disabled = !isAvailable;
    }

    this.updateBundlePrices(option);
    this.updateAddButtonLabel(option.dataset.price || '');

    if (this.dynamicCheckout) this.dynamicCheckout.hidden = !isAvailable;
    if (this.stockMessage) this.stockMessage.hidden = isAvailable;
    if (this.quantityInput) this.quantityInput.disabled = !isAvailable;
    if (this.quantityMinus) this.quantityMinus.disabled = !isAvailable;
    if (this.quantityPlus) this.quantityPlus.disabled = !isAvailable;
    this.bundleOptions.forEach((button) => {
      button.disabled = !isAvailable;
    });

    this.syncOptionButtons(option);

    if (option.dataset.imageUrl && this.mainImage) {
      const galleryIndex = Number.parseInt(option.dataset.galleryIndex || '-1', 10);
      const imagePosition = Number.parseInt(option.dataset.imagePosition || '0', 10) - 1;
      if (this.isFluffRoller && this.isGrayVariant(option)) {
        this.showImageAtIndex(0);
      } else if (galleryIndex >= 0 && galleryIndex < this.thumbnails.length) {
        this.showImageAtIndex(galleryIndex);
      } else if (imagePosition >= 0 && imagePosition < this.thumbnails.length) {
        this.showImageAtIndex(imagePosition);
      } else {
        this.mainImage.removeAttribute('srcset');
        this.mainImage.removeAttribute('sizes');
        this.mainImage.src = option.dataset.imageUrl;
      }
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', option.value);
    window.history.replaceState({}, '', url);
  };

  isGrayVariant(option) {
    return [option.dataset.option1, option.dataset.option2, option.dataset.option3]
      .filter(Boolean)
      .some((value) => /gr[ae]y/i.test(value));
  }

  handleBundleOptionClick = (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement) || !this.quantityInput || button.disabled) return;

    const quantity = Number.parseInt(button.dataset.quantity || '1', 10);
    this.quantityInput.value = String(quantity);
    this.bundleOptions.forEach((option) => {
      option.setAttribute('aria-pressed', String(option === button));
    });
    this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.updateAddButtonLabel();
  };

  handleOptionButtonClick = (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement) || !this.variantSelect) return;

    const position = Number.parseInt(button.dataset.optionPosition || '0', 10);
    const value = button.dataset.optionValue || '';
    if (!position || !value) return;

    const selectedValues = Array.from(this.querySelectorAll('[data-ch-option-group]')).map((group) => {
      const selectedButton = group.querySelector('[data-ch-option-button][aria-pressed="true"]');
      return selectedButton?.dataset.optionValue || '';
    });
    selectedValues[position - 1] = value;

    const variantOptions = Array.from(this.variantSelect.options);
    let matchedVariant = variantOptions.find((option) =>
      selectedValues.every((selectedValue, index) => {
        if (!selectedValue) return true;
        return option.getAttribute(`data-option-${index + 1}`) === selectedValue;
      })
    );

    if (!matchedVariant) {
      matchedVariant = variantOptions.find(
        (option) => option.getAttribute(`data-option-${position}`) === value
      );
    }
    if (!matchedVariant) return;

    this.variantSelect.value = matchedVariant.value;
    this.syncOptionButtons(matchedVariant);
    this.variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
  };

  syncOptionButtons(option) {
    const variantOptions = Array.from(this.variantSelect?.options || []);
    const selectedValues = Array.from(
      { length: this.querySelectorAll('[data-ch-option-group]').length },
      (_, index) => option.getAttribute(`data-option-${index + 1}`) || ''
    );

    this.querySelectorAll('[data-ch-option-group]').forEach((group) => {
      const position = Number.parseInt(group.dataset.optionPosition || '0', 10);
      const selectedValue = option.getAttribute(`data-option-${position}`) || '';

      group.querySelectorAll('[data-ch-option-button]').forEach((button) => {
        const buttonValue = button.dataset.optionValue || '';
        button.setAttribute('aria-pressed', String(buttonValue === selectedValue));

        const matchingVariant =
          variantOptions.find((variantOption) => {
            if (variantOption.getAttribute(`data-option-${position}`) !== buttonValue) {
              return false;
            }

            return selectedValues.every((value, index) => {
              const optionPosition = index + 1;
              if (optionPosition === position || !value) return true;
              return variantOption.getAttribute(`data-option-${optionPosition}`) === value;
            });
          }) ||
          variantOptions.find(
            (variantOption) =>
              variantOption.getAttribute(`data-option-${position}`) === buttonValue
          );

        if (!matchingVariant) return;

        const isAvailable = matchingVariant.dataset.available === 'true';
        button.dataset.available = String(isAvailable);

        const price = button.querySelector('[data-ch-option-price]');
        if (price) price.textContent = matchingVariant.dataset.price || '';

        const stock = button.querySelector('[data-ch-option-stock]');
        if (stock) stock.textContent = isAvailable ? 'In stock' : 'Out of stock';

        const image = button.querySelector('[data-ch-option-image]');
        if (image && matchingVariant.dataset.imageUrl) {
          image.style.setProperty(
            '--ch-option-image',
            `url("${matchingVariant.dataset.imageUrl}")`
          );
        }
      });

      const selectedLabel = group.querySelector('[data-ch-selected-option]');
      if (selectedLabel) selectedLabel.textContent = this.formatOptionLabel(selectedValue);
    });
  }

  formatOptionLabel(value) {
    if (!this.isFluffRoller) return value;

    return value
      .replace(/\s+(pet\s+)?brush/gi, '')
      .replace(/fruit green/i, 'Green')
      .replace(/lake blue/i, 'Blue')
      .trim();
  }

  updateBundlePrices(option = this.variantSelect?.selectedOptions[0]) {
    if (!option || this.bundleOptions.length === 0) return;

    this.bundleOptions.forEach((button) => {
      const quantity = Number.parseInt(button.dataset.quantity || '1', 10);
      const total = option.dataset[`bundle${quantity}Total`] || '';
      const price = button.querySelector('[data-ch-bundle-price]');
      if (total) {
        button.dataset.total = total;
        if (price) price.textContent = total;
      }
    });
  }

  updateAddButtonLabel(unitPrice = this.variantSelect?.selectedOptions[0]?.dataset.price || '') {
    if (!this.addButtonLabel) return;
    if (!this.isAvailable) {
      this.addButtonLabel.textContent = 'Out of stock';
      return;
    }

    const quantity = Number.parseInt(this.quantityInput?.value || '1', 10);
    const selectedBundle = this.bundleOptions.find(
      (button) => button.getAttribute('aria-pressed') === 'true'
    );
    const total = selectedBundle?.dataset.total || unitPrice;
    this.addButtonLabel.textContent = quantity > 1
      ? `Add ${quantity} to cart${total ? ` — ${total}` : ''}`
      : `Add to cart${unitPrice ? ` — ${unitPrice}` : ''}`;
  }

  handleSubmit = async (event) => {
    if (
      event.submitter &&
      event.submitter !== this.addButton
    ) {
      return;
    }

    event.preventDefault();

    if (!this.isAvailable) {
      if (this.stockMessage) {
        this.stockMessage.hidden = false;
        this.stockMessage.setAttribute('role', 'alert');
      }
      return;
    }

    if (
      this.isSubmitting ||
      !this.productForm ||
      !this.variantId
    ) {
      return;
    }

    this.isSubmitting = true;
    this.animateProductToCart();

    const originalButtonLabel =
      this.addButtonLabel?.textContent || 'Add to cart';
    const formData = new FormData(this.productForm);
    const quantity = Number(formData.get('quantity')) || 1;
    const variantId = String(formData.get('id') || '');
    const sectionIds = Array.from(
      document.querySelectorAll('cart-items-component')
    )
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);

    if (sectionIds.length > 0) {
      formData.set('sections', sectionIds.join(','));
      formData.set('sections_url', window.location.pathname);
    }

    const deferredEvent = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [
          {
            merchandiseId: variantId,
            quantity,
          },
        ],
        promise: deferredEvent.promise,
      })
    );

    if (this.addButton) {
      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-busy', 'true');
    }
    if (this.addButtonLabel) {
      this.addButtonLabel.textContent = 'Adding…';
    }

    try {
      const requestConfig = fetchConfig('javascript', {
        body: formData,
      });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          Accept: 'text/html',
        },
      });
      const responseData = await response.json();

      if (!response.ok || responseData.status) {
        const message =
          responseData.description ||
          responseData.message ||
          'This item could not be added to your cart.';

        this.dispatchEvent(
          new CartErrorEvent({
            error: message,
            code: 'INVALID',
            detail: {
              description: responseData.description,
              errors: responseData.errors,
            },
          })
        );

        if (this.stockMessage) {
          this.stockMessage.textContent = message;
          this.stockMessage.hidden = false;
          this.stockMessage.setAttribute('role', 'alert');
        }

        const cartResponse = await fetch(
          `${Theme.routes.cart_url}.js`,
          {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          }
        );
        const cartData = await cartResponse.json();

        deferredEvent.resolve({
          cart:
            CartLinesUpdateEvent.createCartFromAjaxResponse(
              cartData
            ),
          detail: {
            didError: true,
            items: cartData.items,
            itemCount: quantity,
            source: 'choreless-product',
          },
        });
        return;
      }

      const cartResponse = await fetch(
        `${Theme.routes.cart_url}.js`,
        {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        }
      );
      const cartData = await cartResponse.json();

      deferredEvent.resolve({
        cart:
          CartLinesUpdateEvent.createCartFromAjaxResponse(
            cartData
          ),
        detail: {
          didError: false,
          items: cartData.items,
          itemCount: quantity,
          sections: responseData.sections,
          source: 'choreless-product',
        },
      });

      if (this.addButtonLabel) {
        this.addButtonLabel.textContent = 'Added to cart';
      }
    } catch (error) {
      deferredEvent.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error:
            error?.message ||
            'Network error while adding this item.',
          code: 'SERVICE_UNAVAILABLE',
        })
      );

      if (this.stockMessage) {
        this.stockMessage.textContent =
          'We could not update your cart. Please try again.';
        this.stockMessage.hidden = false;
        this.stockMessage.setAttribute('role', 'alert');
      }
    } finally {
      window.setTimeout(() => {
        this.isSubmitting = false;

        if (this.addButton) {
          this.addButton.disabled = !this.isAvailable;
          this.addButton.removeAttribute('aria-busy');
        }

        if (this.addButtonLabel) {
          const option =
            this.variantSelect?.selectedOptions[0];
          this.updateAddButtonLabel(
            option?.dataset.price ||
              originalButtonLabel.replace(/^Add to cart —\s*/, '')
          );
        }
      }, 720);
    }
  };

  animateProductToCart() {
    if (
      window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches
    ) {
      return;
    }

    const cartIcon = document.querySelector(
      '.header-actions__cart-icon'
    );
    if (!cartIcon || !this.addButton) return;

    const flyToCartElement = document.createElement(
      'fly-to-cart'
    );
    const imageUrl =
      this.mainImage?.currentSrc || this.mainImage?.src;

    flyToCartElement.classList.add('fly-to-cart--main');
    if (imageUrl) {
      flyToCartElement.style.backgroundImage = `url("${imageUrl}")`;
    }
    flyToCartElement.style.setProperty(
      '--start-opacity',
      '0'
    );
    flyToCartElement.source = this.addButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  showStockMessage() {
    if (this.stockMessage) {
      this.stockMessage.hidden = false;
      this.stockMessage.setAttribute('role', 'alert');
    }
  }

  handleThumbnailClick = (event) => {
    const thumbnail = event.currentTarget;
    if (!(thumbnail instanceof HTMLButtonElement) || !this.mainImage) return;

    const imageIndex = this.thumbnails.indexOf(thumbnail);
    if (imageIndex >= 0) this.showImageAtIndex(imageIndex);
  };

  showImageAtIndex(index) {
    const thumbnail = this.thumbnails[index];
    if (!thumbnail || !this.mainImage || !thumbnail.dataset.imageUrl) return;

    const imageUrl =
      this.prefersReducedMotion.matches && thumbnail.dataset.reducedMotionUrl
        ? thumbnail.dataset.reducedMotionUrl
        : thumbnail.dataset.imageUrl;
    this.mainImage.removeAttribute('srcset');
    this.mainImage.removeAttribute('sizes');
    this.mainImage.src = imageUrl;
    this.mainImage.alt = thumbnail.dataset.imageAlt || this.mainImage.alt;
    this.mainImage.classList.toggle(
      'ch-product__main-image--cover',
      thumbnail.dataset.imageFit === 'cover'
    );
    if (this.stageBadge) {
      this.stageBadge.textContent = thumbnail.dataset.imageBadge || '';
      this.stageBadge.hidden = !thumbnail.dataset.imageBadge;
    }
    this.currentImageIndex = index;

    this.thumbnails.forEach((item, thumbnailIndex) => {
      item.setAttribute('aria-current', String(thumbnailIndex === index));
    });
    this.syncMobileGallery(index);
    this.updateImageCounter();
  }

  updateImageCounter() {
    if (this.mobileImageCounter) {
      this.mobileImageCounter.textContent = String(this.currentImageIndex + 1);
    }
  }

  handleMobileGalleryScroll = () => {
    if (!this.mobileGallery || this.mobileScrollFrame) return;

    this.mobileScrollFrame = window.requestAnimationFrame(() => {
      this.mobileScrollFrame = null;
      const scrollPosition = this.mobileGallery.scrollLeft;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.mobileSlides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - scrollPosition);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      this.currentImageIndex = closestIndex;
      this.thumbnails.forEach((thumbnail, index) => {
        thumbnail.setAttribute('aria-current', String(index === closestIndex));
      });
      this.updateImageCounter();
    });
  };

  syncMobileGallery(index, smooth = true) {
    const slide = this.mobileSlides[index];
    if (!this.mobileGallery || !slide || this.mobileGallery.offsetWidth === 0) return;

    this.mobileGallery.scrollTo({
      left: slide.offsetLeft,
      behavior: smooth ? 'smooth' : 'auto',
    });
  }

  decreaseQuantity = () => {
    this.adjustQuantity(-1);
  };

  increaseQuantity = () => {
    this.adjustQuantity(1);
  };

  adjustQuantity(direction) {
    if (!this.quantityInput) return;

    const minimum = Number.parseInt(this.quantityInput.min || '1', 10);
    const step = Number.parseInt(this.quantityInput.step || '1', 10);
    const current = Number.parseInt(this.quantityInput.value || String(minimum), 10);
    const next = Math.max(minimum, current + direction * step);

    this.quantityInput.value = String(next);
    this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('choreless-product')) {
  customElements.define('choreless-product', ChorelessProduct);
}
